import type { ClassCount, Scholarship } from '../../../../types/scholarships';
import { classDisplayLabel } from './scholarshipClasses';
import { formatMoney } from './scholarshipMoney';
import { POSITION_GROUPS, type PositionGroup, positionToGroup } from './positionGroups';
import { resolveVisibleCards, storedCards, type SummaryCardKey } from './summaryCards';
import { tourAnchor } from '../../../tours/tourAnchors';
import { configureBoardLabel } from './boardPurpose';
import './scholarshipSummary.css';

interface ScholarshipSummaryCardProps {
  scholarship: Scholarship;
}

const DASH = '—';

/**
 * Formats an allocation value (used/available/remaining) per the active model.
 * Currency formatting is used only for the dollar-based Percentage model ("Dollar
 * Budget Model" in the UI); the count-based FullHalf and Equivalency models use
 * one-decimal counts.
 */
const formatAllocation = (
  value: number | null | undefined,
  isPercentageModel: boolean,
): string => {
  if (value == null) return DASH;
  return isPercentageModel ? formatMoney(value) : value.toFixed(1);
};

// Which model is active, derived once per card rather than threaded through props.
const modelFlags = (scholarshipModel: string) => ({
  isPercentageModel: scholarshipModel === 'Percentage',
  isEquivalencyModel: scholarshipModel === 'Equivalency',
});

// NIL Budget / Revenue Share Budget card — same shape as the allocation card but
// always dollar-formatted and model-independent. Which rosters may show it is decided by
// `availableWhen` in summaryCards.ts, not here.
interface MoneyBucketCardProps {
  title: string;
  bucketName: string; // 'NIL' | 'Rev Share' — used in the badge text
  budget: number | null;
  used: number;
  remaining: number | null;
  percentUsed: number | null;
  isOver: boolean;
  testId: string;
}

const MoneyBucketCard = ({
  title,
  bucketName,
  budget,
  used,
  remaining,
  percentUsed,
  isOver,
  testId,
}: MoneyBucketCardProps) => {
  const hasBudget = budget != null;
  const truePercent = percentUsed ?? 0;
  const barWidth = Math.min(Math.max(truePercent, 0), 100);

  return (
    <article className="scholarship-summary__card" data-testid={`summary-${testId}-card`}>
      <header className="scholarship-summary__card-header">
        <h3 className="scholarship-summary__card-title">{title}</h3>
      </header>

      <div className="scholarship-summary__stats">
        <div className="scholarship-summary__stat">
          <span className="scholarship-summary__stat-label">Budget</span>
          <span
            className="scholarship-summary__stat-value"
            data-testid={`summary-${testId}-budget`}
          >
            {hasBudget ? formatMoney(budget) : 'No budget set'}
          </span>
        </div>
        <div className="scholarship-summary__stat">
          <span className="scholarship-summary__stat-label">Used</span>
          <span
            className="scholarship-summary__stat-value"
            data-testid={`summary-${testId}-used`}
          >
            {formatMoney(used)}
          </span>
        </div>
        <div className="scholarship-summary__stat">
          <span className="scholarship-summary__stat-label">Remaining</span>
          <span
            className="scholarship-summary__stat-value"
            data-testid={`summary-${testId}-remaining`}
          >
            {remaining == null ? DASH : formatMoney(remaining)}
          </span>
        </div>
      </div>

      {hasBudget && (
        <>
          <div
            className="scholarship-summary__bar-track"
            role="progressbar"
            aria-valuenow={truePercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${title} used`}
            data-testid={`summary-${testId}-bar`}
          >
            <div
              className={
                isOver
                  ? 'scholarship-summary__bar-fill scholarship-summary__bar-fill--over'
                  : 'scholarship-summary__bar-fill'
              }
              style={{ width: `${barWidth}%` }}
            />
          </div>
          <p className="scholarship-summary__bar-caption">
            {truePercent}% of Budget Used
          </p>
          <span
            className={
              isOver
                ? 'scholarship-summary__badge scholarship-summary__badge--over'
                : 'scholarship-summary__badge scholarship-summary__badge--within'
            }
            data-testid={`summary-${testId}-badge`}
          >
            {isOver ? `Over ${bucketName} Budget` : `Within ${bucketName} Budget`}
          </span>
        </>
      )}
    </article>
  );
};

/**
 * Position needs — committed against wanted, per position group.
 *
 * Aggregates the API's per-position rows into the groups the depth chart already uses, because
 * a coach plans "three middle infielders", not "one SS and two 2B". The underlying needs are
 * still stored per exact position so they line up with the per-position budgets.
 */
const PositionNeedsCard = ({ scholarship }: ScholarshipSummaryCardProps) => {
  const rows = scholarship.summary.positionBudgets.filter(
    (pb) => pb.playersNeeded != null || pb.committedCount > 0,
  );

  if (rows.length === 0) {
    return (
      <article className="scholarship-summary__card" data-testid="summary-needs-card">
        <header className="scholarship-summary__card-header">
          <h3 className="scholarship-summary__card-title">Position Needs</h3>
        </header>
        <p className="scholarship-summary__model-desc">
          No position needs set yet. Add them under {configureBoardLabel(scholarship)}.
        </p>
      </article>
    );
  }

  // Group in the depth chart's order so the eye does not have to jump between the two views.
  const groups = new Map<PositionGroup, { needed: number; committed: number }>();
  for (const pb of rows) {
    const group = positionToGroup(pb.position);
    const current = groups.get(group) ?? { needed: 0, committed: 0 };
    groups.set(group, {
      needed: current.needed + (pb.playersNeeded ?? 0),
      committed: current.committed + pb.committedCount,
    });
  }
  const ordered = POSITION_GROUPS.filter((g) => groups.has(g));

  return (
    <article className="scholarship-summary__card" data-testid="summary-needs-card">
      <header className="scholarship-summary__card-header">
        <h3 className="scholarship-summary__card-title">Position Needs</h3>
      </header>

      <div className="scholarship-summary__needs">
        {ordered.map((group) => {
          const { needed, committed } = groups.get(group)!;
          // Over-recruited is a real state, so the bar caps at full width while the ratio
          // still reads 4/3 rather than being clamped to something tidier than the truth.
          const pct = needed > 0 ? Math.min((committed / needed) * 100, 100) : 0;
          const isMet = needed > 0 && committed >= needed;

          return (
            <div className="scholarship-summary__need" key={group}>
              <span className="scholarship-summary__need-label">{group}</span>
              <span
                className="scholarship-summary__need-ratio"
                data-testid={`summary-need-${group}`}
              >
                {committed}/{needed > 0 ? needed : DASH}
              </span>
              <div
                className="scholarship-summary__bar-track"
                role="progressbar"
                aria-valuenow={committed}
                aria-valuemin={0}
                aria-valuemax={needed > 0 ? needed : committed}
                aria-label={`${group} committed`}
              >
                <div
                  className={
                    isMet
                      ? 'scholarship-summary__bar-fill scholarship-summary__bar-fill--met'
                      : 'scholarship-summary__bar-fill'
                  }
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <p className="scholarship-summary__bar-caption">Committed / needed</p>
    </article>
  );
};

/** Targets, commits and offers out for the whole board. */
const ProgressCard = ({ scholarship }: ScholarshipSummaryCardProps) => {
  const { summary } = scholarship;
  const pct = summary.committedPercentOfTarget;
  const barWidth = Math.min(Math.max(pct ?? 0, 0), 100);

  return (
    <article className="scholarship-summary__card" data-testid="summary-progress-card">
      <header className="scholarship-summary__card-header">
        <h3 className="scholarship-summary__card-title">Recruiting Progress</h3>
      </header>

      <div className="scholarship-summary__stats">
        <div className="scholarship-summary__stat">
          <span className="scholarship-summary__stat-label">Targets</span>
          <span
            className="scholarship-summary__stat-value"
            data-testid="summary-progress-targets"
          >
            {summary.targetCount > 0 ? summary.targetCount : DASH}
          </span>
        </div>
        <div className="scholarship-summary__stat">
          <span className="scholarship-summary__stat-label">Committed</span>
          <span
            className="scholarship-summary__stat-value"
            data-testid="summary-progress-committed"
          >
            {summary.committedCount}
          </span>
        </div>
        <div className="scholarship-summary__stat">
          <span className="scholarship-summary__stat-label">Offers out</span>
          <span
            className="scholarship-summary__stat-value"
            data-testid="summary-progress-offered"
          >
            {summary.offeredCount}
          </span>
        </div>
      </div>

      {/*
        No target set means no denominator. Saying so beats rendering a 0% bar, which would
        read as "no progress" rather than "no plan yet".
      */}
      {pct == null ? (
        <p className="scholarship-summary__bar-caption">
          Set position needs to track progress against a target.
        </p>
      ) : (
        <>
          <div
            className="scholarship-summary__bar-track"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Committed against target"
            data-testid="summary-progress-bar"
          >
            <div
              className="scholarship-summary__bar-fill"
              style={{ width: `${barWidth}%` }}
            />
          </div>
          <p className="scholarship-summary__bar-caption">{pct}% committed</p>
        </>
      )}
    </article>
  );
};

/**
 * Class summary — how the board's players spread across whatever is set in Class.
 *
 * Counts both shapes the column holds: a graduation year, from a linked player record or typed
 * in, and a year of eligibility. Excluding the ladder was the original reading, and it left
 * this card permanently empty on every board that counts 1-5.
 *
 * Grouped by whichever classes are actually present rather than by a class the roster declares.
 * A board fills up with mostly one and a couple of others, so a single declared class would
 * have been a lie about the roster in front of the coach.
 */
/*
  classLabel is what this reads, but an API that predates it sends only gradYear — web and API
  deploy as separate jobs, so that window is real. Falling back keeps the card rendering the
  buckets it always could instead of a row of "undefined".
*/
const classCountLabel = (c: ClassCount): string =>
  c.classLabel ?? (c.gradYear != null ? String(c.gradYear) : '—');

const ClassSummaryCard = ({ scholarship }: ScholarshipSummaryCardProps) => {
  const breakdown = scholarship.summary.classBreakdown;

  return (
    <article className="scholarship-summary__card" data-testid="summary-class-card">
      <header className="scholarship-summary__card-header">
        <h3 className="scholarship-summary__card-title">Class Summary</h3>
      </header>

      {breakdown.length === 0 ? (
        <p className="scholarship-summary__model-desc">
          Set a class on a player, or attach players from your player list, to see the
          breakdown here.
        </p>
      ) : (
        /*
          Its own grid rather than the shared stat row. Every other card pairs a wordy label with
          one number, so content-width columns suit them; here the labels are a digit and the
          counts are one or two, and the pairs drifted out of line with each other the moment a
          count reached double figures. Equal columns are what make a distribution readable.
        */
        <div className="scholarship-summary__class-grid">
          {breakdown.map((c) => {
            const label = classCountLabel(c);
            return (
              <div className="scholarship-summary__class-cell" key={label}>
                <span className="scholarship-summary__class-label">{classDisplayLabel(label)}</span>
                <span
                  className="scholarship-summary__class-count"
                  data-testid={`summary-class-${label}`}
                >
                  {c.count}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {breakdown.length > 0 && (
        <p className="scholarship-summary__bar-caption">
          {breakdown.reduce((sum, c) => sum + c.count, 0)} players with a class
        </p>
      )}
    </article>
  );
};

const RosterSpotsCard = ({ scholarship }: ScholarshipSummaryCardProps) => {
  const { summary, rosterLimit } = scholarship;
  const hasRosterLimit = rosterLimit != null;
  const allowedLabel = hasRosterLimit ? String(rosterLimit) : 'No limit';
  const remainingLabel =
    summary.rosterRemaining == null ? DASH : String(summary.rosterRemaining);

  return (
    <article className="scholarship-summary__card" data-testid="summary-roster-card">
      <header className="scholarship-summary__card-header">
        <h3 className="scholarship-summary__card-title">Roster Spots</h3>
      </header>

      <div className="scholarship-summary__stats">
        <div className="scholarship-summary__stat">
          <span className="scholarship-summary__stat-label">Allowed</span>
          <span
            className="scholarship-summary__stat-value"
            data-testid="summary-roster-allowed"
          >
            {allowedLabel}
          </span>
        </div>
        <div className="scholarship-summary__stat">
          <span className="scholarship-summary__stat-label">Filled</span>
          <span
            className="scholarship-summary__stat-value"
            data-testid="summary-roster-filled"
          >
            {summary.rosterFilled}
          </span>
        </div>
        <div className="scholarship-summary__stat">
          <span className="scholarship-summary__stat-label">Remaining</span>
          <span
            className="scholarship-summary__stat-value"
            data-testid="summary-roster-remaining"
          >
            {remainingLabel}
          </span>
        </div>
      </div>

      {hasRosterLimit && (
        <span
          className={
            summary.isOverRosterLimit
              ? 'scholarship-summary__badge scholarship-summary__badge--over'
              : 'scholarship-summary__badge scholarship-summary__badge--within'
          }
          data-testid="summary-roster-badge"
        >
          {summary.isOverRosterLimit ? 'Over Roster Limit' : 'Within Roster Limit'}
        </span>
      )}
    </article>
  );
};

const AllocationCard = ({ scholarship }: ScholarshipSummaryCardProps) => {
  const { summary } = scholarship;
  const { isPercentageModel, isEquivalencyModel } = modelFlags(scholarship.scholarshipModel);

  // 3-way: Percentage/dollar model (dollars), Equivalency (counts), FullHalf (counts).
  // The Percentage model surfaces as "Dollar Budget Model" in the UI.
  const hasBudget = summary.allocationAvailable != null;
  const allocationSuffix = isPercentageModel
    ? '(DOLLAR MODEL)'
    : isEquivalencyModel
      ? '(EQUIVALENCY)'
      : '(FULL / HALF)';
  const allocationSublabel = isPercentageModel
    ? 'Scholarship Budget'
    : isEquivalencyModel
      ? 'Scholarships'
      : 'Scholarship Units';
  const truePercent = summary.allocationPercentUsed ?? 0;
  const barWidth = Math.min(Math.max(truePercent, 0), 100);

  return (
    <article className="scholarship-summary__card" data-testid="summary-allocation-card">
      <header className="scholarship-summary__card-header">
        <h3 className="scholarship-summary__card-title">
          Scholarship Allocation{' '}
          <span className="scholarship-summary__card-suffix">{allocationSuffix}</span>
        </h3>
      </header>

      <div className="scholarship-summary__stats">
        <div className="scholarship-summary__stat">
          <span className="scholarship-summary__stat-label">Available</span>
          <span
            className="scholarship-summary__stat-value"
            data-testid="summary-allocation-available"
          >
            {hasBudget
              ? formatAllocation(summary.allocationAvailable, isPercentageModel)
              : 'No budget set'}
          </span>
        </div>
        <div className="scholarship-summary__stat">
          <span className="scholarship-summary__stat-label">Used</span>
          <span
            className="scholarship-summary__stat-value"
            data-testid="summary-allocation-used"
          >
            {formatAllocation(summary.allocationUsed, isPercentageModel)}
          </span>
        </div>
        <div className="scholarship-summary__stat">
          <span className="scholarship-summary__stat-label">Remaining</span>
          <span
            className="scholarship-summary__stat-value"
            data-testid="summary-allocation-remaining"
          >
            {formatAllocation(summary.allocationRemaining, isPercentageModel)}
          </span>
        </div>
      </div>

      <p className="scholarship-summary__sublabel">{allocationSublabel}</p>

      {hasBudget && (
        <>
          <div
            className="scholarship-summary__bar-track"
            role="progressbar"
            aria-valuenow={truePercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Allocation used"
            data-testid="summary-allocation-bar"
          >
            <div
              className={
                summary.isOverBudget
                  ? 'scholarship-summary__bar-fill scholarship-summary__bar-fill--over'
                  : 'scholarship-summary__bar-fill'
              }
              style={{ width: `${barWidth}%` }}
            />
          </div>
          <p className="scholarship-summary__bar-caption">
            {truePercent}% of Allocation Used
          </p>
          <span
            className={
              summary.isOverBudget
                ? 'scholarship-summary__badge scholarship-summary__badge--over'
                : 'scholarship-summary__badge scholarship-summary__badge--within'
            }
            data-testid="summary-allocation-badge"
          >
            {summary.isOverBudget
              ? 'Over Scholarship Limit'
              : 'Within Scholarship Limit'}
          </span>
        </>
      )}

      {(summary.averageValue != null || summary.averageTarget != null) && (
        <div className="scholarship-summary__average" data-testid="summary-average">
          <div className="scholarship-summary__stats">
            <div className="scholarship-summary__stat">
              <span className="scholarship-summary__stat-label">Avg / Player</span>
              <span
                className="scholarship-summary__stat-value"
                data-testid="summary-average-current"
              >
                {summary.averageValue == null
                  ? DASH
                  : formatAllocation(summary.averageValue, isPercentageModel)}
              </span>
            </div>
            <div className="scholarship-summary__stat">
              <span className="scholarship-summary__stat-label">Target</span>
              <span
                className="scholarship-summary__stat-value"
                data-testid="summary-average-target"
              >
                {summary.averageTarget == null
                  ? DASH
                  : formatAllocation(summary.averageTarget, isPercentageModel)}
              </span>
            </div>
          </div>
          {summary.averageTarget != null && (
            <span
              className={
                summary.isOverAverageTarget
                  ? 'scholarship-summary__badge scholarship-summary__badge--over'
                  : 'scholarship-summary__badge scholarship-summary__badge--within'
              }
              data-testid="summary-average-badge"
            >
              {summary.isOverAverageTarget
                ? 'Over Target Average'
                : 'Within Target Average'}
            </span>
          )}
        </div>
      )}
    </article>
  );
};

const ModelCard = ({ scholarship }: ScholarshipSummaryCardProps) => {
  const { isPercentageModel, isEquivalencyModel } = modelFlags(scholarship.scholarshipModel);

  return (
    <article className="scholarship-summary__card" data-testid="summary-model-card">
      <header className="scholarship-summary__card-header">
        <h3 className="scholarship-summary__card-title">Scholarship Model</h3>
      </header>

      {isPercentageModel ? (
        <>
          <p className="scholarship-summary__model-name" data-testid="summary-model-name">
            Dollar Budget Model
          </p>
          <p className="scholarship-summary__model-desc">
            Set total dollar budgets and assign actual dollar amounts.
          </p>
        </>
      ) : isEquivalencyModel ? (
        <>
          <p className="scholarship-summary__model-name" data-testid="summary-model-name">
            Equivalency Model
          </p>
          <p className="scholarship-summary__model-desc">
            Set a total number of scholarships; each player is assigned a percentage.
          </p>
        </>
      ) : (
        <>
          <p className="scholarship-summary__model-name" data-testid="summary-model-name">
            Full / Half Scholarship Model
          </p>
          <p className="scholarship-summary__model-desc">
            Each player is assigned a scholarship unit.
          </p>
          <dl className="scholarship-summary__model-mapping">
            <div className="scholarship-summary__model-mapping-row">
              <dt>Full Scholarship</dt>
              <dd className="scholarship-summary__stat-value">1.0</dd>
            </div>
            <div className="scholarship-summary__model-mapping-row">
              <dt>Half Scholarship</dt>
              <dd className="scholarship-summary__stat-value">0.5</dd>
            </div>
          </dl>
        </>
      )}
    </article>
  );
};

/**
 * The summary band above the board.
 *
 * Which cards appear is the roster's own setting (`visibleCards`, chosen via
 * SummaryCardPicker) rather than a fixed sequence, so a coach tracking money and a coach
 * tracking needs can each put what they care about at the top of the page.
 *
 * `resolveVisibleCards` does the filtering: it drops keys this build does not recognise and
 * keys the roster cannot show. Availability is checked at render and never written back, so
 * turning NIL tracking off and on again restores the card the coach had chosen.
 */
export const ScholarshipSummaryCard = ({ scholarship }: ScholarshipSummaryCardProps) => {
  const { summary } = scholarship;
  const visible = resolveVisibleCards(storedCards(scholarship), scholarship);

  // Every card turned off is a real choice, so the band disappears entirely rather than
  // leaving a heading over empty space. The picker lives in the page header, not in here, so
  // it stays reachable.
  if (visible.length === 0) return null;

  const renderCard = (key: SummaryCardKey) => {
    switch (key) {
      case 'positionNeeds':
        return <PositionNeedsCard key={key} scholarship={scholarship} />;
      case 'progress':
        return <ProgressCard key={key} scholarship={scholarship} />;
      case 'classSummary':
        return <ClassSummaryCard key={key} scholarship={scholarship} />;
      case 'rosterSpots':
        return <RosterSpotsCard key={key} scholarship={scholarship} />;
      case 'allocation':
        return <AllocationCard key={key} scholarship={scholarship} />;
      case 'nil':
        return (
          <MoneyBucketCard
            key={key}
            title="NIL Budget"
            bucketName="NIL"
            budget={scholarship.nilBudget}
            used={summary.nilUsed}
            remaining={summary.nilRemaining}
            percentUsed={summary.nilPercentUsed}
            isOver={summary.isOverNilBudget}
            testId="nil"
          />
        );
      case 'revShare':
        return (
          <MoneyBucketCard
            key={key}
            title="Revenue Share Budget"
            bucketName="Rev Share"
            budget={scholarship.revShareBudget}
            used={summary.revShareUsed}
            remaining={summary.revShareRemaining}
            percentUsed={summary.revSharePercentUsed}
            isOver={summary.isOverRevShareBudget}
            testId="rev-share"
          />
        );
      case 'model':
        return <ModelCard key={key} scholarship={scholarship} />;
    }
  };

  return (
    <section
      className="scholarship-summary"
      aria-label="Scholarship and roster summary"
      data-testid="scholarship-summary-card"
      {...tourAnchor('rosters.scholarship-total')}
    >
      <h2 className="scholarship-summary__band-title">Scholarship &amp; Roster Summary</h2>

      <div className="scholarship-summary__grid">{visible.map(renderCard)}</div>
    </section>
  );
};
